/**
 * Polish message catalog — mirrors the shape of `messages.en.ts`
 * exactly; `satisfies MessagesShape` makes a missing or renamed key a
 * TypeScript error. Register: ty (informal, locked in l10n/locales.md).
 *
 * Platform terms follow Polish UI conventions (ustawienia, parowanie
 * for pairing, dublowanie for notification mirroring, usuń for dismiss,
 * rozmowa for conversation, schowek for clipboard, odbiornik for a
 * receiving surface). "Glance" is the untranslated feature brand name
 * (glossary D1); „klip” renders the D2 loanword in native script.
 *
 * Plural notes: Polish is a {one, few, many, other} CLDR locale —
 * every count-bearing `selectPlural("pl", …)` supplies all four forms.
 * `one` covers exactly 1; `few` = 2–4, 22–24…; `many` = 0, 5–21…;
 * `other` = fractions. The count is never hardcoded into any form.
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "Połączono",
    statusReconnecting: "Ponowne łączenie…",
    statusDisconnected: "Rozłączono",
    openSettings: "Otwórz ustawienia",
    pairedFallbackDevice: "twój telefon",
    publicModeToggleEnable: "Ukryj treść powiadomień",
    publicModeToggleDisable: "Pokaż treść powiadomień",
  },

  notifications: {
    replyAriaLabel: "Odpowiedz",
    replyPlaceholder: "Odpowiedz…",
    replySend: "Wyślij",
    sent: "Wysłano",
    failedToSend: "Nie udało się wysłać",
    unread: "Nieprzeczytane",
    dismiss: "Usuń",
    notificationImageAlt: "Obraz powiadomienia",
  },

  conversations: {
    replyAriaLabel: "Odpowiedz",
    replyPlaceholder: "Odpowiedz…",
    replySend: "Wyślij",
    sent: "Wysłano",
    failedToSend: "Nie udało się wysłać",
    dismiss: "Usuń rozmowę",
    messagesListLabel: "Wiadomości w tej rozmowie",
    deviceOwner: "Ty",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "Brak nieprzeczytanych wiadomości"
        : `${count} ${selectPlural("pl", count, {
            one: "nieprzeczytana wiadomość",
            few: "nieprzeczytane wiadomości",
            many: "nieprzeczytanych wiadomości",
            other: "nieprzeczytanej wiadomości",
          })}`,
  },

  list: {
    today: "Dziś",
    yesterday: "Wczoraj",
    searchNotifications: "Szukaj powiadomień",
    clearSearch: "Wyczyść wyszukiwanie",
    clearAll: "Usuń wszystkie",
    markAllRead: "Oznacz wszystkie jako przeczytane",
    groupCheckboxLabel: "Grupuj",
    noNotificationsTitle: "Brak powiadomień",
    noNotificationsDesc: "Nowe wiadomości zdublowane z twojego telefonu pojawią się tutaj.",
    noMatchesTitle: "Brak wyników",
    notificationsList: "Powiadomienia",
    privacyAppList: "Powiadomienia według aplikacji",
    clear: "Usuń",
    clearMatches: (count: number): string =>
      `Usuń ${count} ${selectPlural("pl", count, {
        one: "dopasowanie",
        few: "dopasowania",
        many: "dopasowań",
        other: "dopasowania",
      })}`,
    noMatchesDesc: (query: string): string => `Brak powiadomień pasujących do „${query}”.`,
    appNotificationsGroup: (appName: string): string => `Powiadomienia ${appName}`,
    dismissAllFrom: (appName: string): string => `Usuń wszystkie z ${appName}`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("pl", count, {
              one: "wiadomość",
              few: "wiadomości",
              many: "wiadomości",
              other: "wiadomości",
            })
          : kind === "calls"
            ? selectPlural("pl", count, {
                one: "połączenie",
                few: "połączenia",
                many: "połączeń",
                other: "połączenia",
              })
            : selectPlural("pl", count, {
                one: "powiadomienie",
                few: "powiadomienia",
                many: "powiadomień",
                other: "powiadomienia",
              });
      return `${count} ${noun}`;
    },
    kbdDismiss: "usuń",
    kbdNavigate: "nawigacja",
    kbdOrSeparator: "lub",
  },

  settings: {
    back: "Powrót do powiadomień",
    settings: "Ustawienia",
    appearance: "Wygląd",
    theme: "Motyw",
    themeAuto: "Automatyczny",
    themeLight: "Jasny",
    themeDark: "Ciemny",
    language: "Język",
    languageSystemDefault: "Jak w systemie",
    notificationsSection: "Powiadomienia",
    osNotifications: "Powiadomienia systemowe",
    callNotifications: "Powiadomienia o połączeniach",
    privacySection: "Prywatność",
    analyticsToggle: "Anonimowe statystyki użytkowania",
    analyticsToggleDescription: "Tylko liczby i kamienie milowe — nigdy twoje powiadomienia.",
    connection: "Połączenie",
    status: "Status",
    statusConnected: "Połączono",
    statusReconnecting: "Ponowne łączenie…",
    statusDisconnected: "Rozłączono",
    disconnectFromPhone: "Odłącz od telefonu",
    disconnectConfirm: "Na pewno? Spowoduje to anulowanie parowania twojego urządzenia.",
    yesDisconnect: "Tak, odłącz",
    cancel: "Anuluj",
    aboutAndSupport: "O aplikacji i pomoc",
    version: "Wersja",
    encryption: "Szyfrowanie",
    encryptionValue: "E2E (NaCl)",
    needHelp: "Potrzebujesz pomocy? Napisz na __EMAIL__",
    disconnectFailed: "Nie udało się rozłączyć — spróbuj ponownie",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `Rozłączono, ale nie udało się wyczyścić danych lokalnych: ${errorMsg}. Odśwież stronę.`,
    disconnectLocalCleanupFailed:
      "Rozłączono, ale nie udało się wyczyścić danych lokalnych. Odśwież stronę.",
  },

  pairing: {
    enterCodeInstruction: "Wprowadź ten kod w aplikacji Reflecto",
    pairedSuccessfully: "Sparowano pomyślnie!",
    pairingFailed: "Parowanie nie powiodło się",
    requestingPairingCode: "Uzyskiwanie kodu parowania…",
    codeExpired: "Kod wygasł",
    tryAgain: "Spróbuj ponownie",
    failedToStartPairing: "Nie udało się rozpocząć parowania",
    needAppText: "Potrzebujesz aplikacji na Androida? Napisz na __EMAIL__, aby uzyskać dostęp.",
  },

  dnd: {
    title: "Nie przeszkadzać",
    description: "Wstrzymaj powiadomienia na tym urządzeniu na najbliższą godzinę.",
    snooze1h: "Wstrzymaj na 1 godzinę",
    snoozeActiveUntil: (formattedTime: string): string => `Aktywne do ${formattedTime}`,
    cancelSnooze: "Anuluj",
  },

  connectors: {
    title: "Połączone usługi",
    emptyBody:
      "Nie masz jeszcze połączonych usług. Połącz ChatGPT lub Claude w ustawieniach ich konektorów, a następnie zatwierdź kod na telefonie.",
    scopeSend: "Może wysyłać powiadomienia",
    added: (date: string): string => `Dodano ${date}`,
    lastUsed: (relative: string): string => `Ostatnio użyto ${relative}`,
    neverUsed: "Nigdy nie użyto",
    revoke: "Odłącz",
    revokeConfirm: (label: string): string => `Odłączyć ${label}? Natychmiast utraci dostęp.`,
    revokeAction: "Odłącz",
    revokeConfirmLabel: (label: string): string => `Odłącz ${label}`,
    cancel: "Anuluj",
    loadError: "Nie udało się załadować połączonych usług. Spróbuj ponownie.",
    revokeError: "Nie udało się odłączyć usługi. Spróbuj ponownie.",
    retry: "Ponów",
  },

  feedback: {
    title: "Wyślij opinię",
    close: "Zamknij",
    messageLabel: "Twoja wiadomość",
    messagePlaceholder: "Widzisz błąd, masz pomysł lub coś jest nie tak? Napisz do nas.",
    categoryLabel: "Kategoria",
    categoryBug: "Błąd",
    categoryIdea: "Pomysł",
    categoryOther: "Inne",
    emailLabel: "E-mail (opcjonalnie, tylko jeśli chcesz otrzymać odpowiedź)",
    emailPlaceholder: "you@example.com",
    emailHelp: "Nie dodamy cię do żadnej listy.",
    disclosure:
      "Twoja wiadomość jest wysyłana na serwer Reflecto i zapisywana w naszym wewnętrznym systemie zgłoszeń. Dołączamy wersję aplikacji oraz identyfikator urządzenia przypisany podczas parowania, aby powiązać zgłoszenia. Nie widzimy twoich powiadomień, kontaktów ani innych danych osobowych. E-mail jest opcjonalny — podaj go tylko, jeśli chcesz otrzymać odpowiedź. Nie dodamy cię do żadnej listy.",
    submit: "Wyślij opinię",
    sending: "Wysyłanie…",
    success: "Dziękujemy — przeczyta to człowiek, zwykle w ciągu 24 godzin",
    error: "Nie udało się wysłać. Spróbuj ponownie.",
    done: "Gotowe",
    sendFeedback: "Wyślij opinię",
  },

  glance: {
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "Zamknij",
    aiSummaryTag: "Podsumowanie AI",
    errorAi: "Nie udało się teraz utworzyć podsumowania.",
    retryCta: "Spróbuj ponownie",
    downloadHeading: "Podsumowania AI",
    downloadBody:
      "Nadrób zaległości dzięki podsumowaniu AI tworzonym na urządzeniu. Wszystko działa w całości na twoim urządzeniu.",
    downloadCta: "Pobierz model",
    downloadPrivacyNote: "Jednorazowe pobranie. Serwer nigdy nie widzi twoich powiadomień.",
    downloadingHeading: "Pobieranie modelu…",
    caughtUp: "Wszystko na bieżąco — nic teraz nie wymaga twojej uwagi.",
    needsYou: "Wymaga uwagi",
    quiet: "Pozostałe",
    scanning: (pending: number): string => `Podsumowuję ${pending}…`,
    jump: "Otwórz powiadomienie",
    affordanceReply: "Odpowiedz",
    affordanceOpen: "Otwórz",
    affordanceCopyOtp: "Kopiuj kod",
    buttonLabel: "Glance",
  },

  update: {
    title: "Dostępna aktualizacja",
    description: "Nowa wersja Reflecto jest gotowa. Odśwież stronę, aby zaktualizować.",
    reload: "Odśwież",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `Nazwaliśmy to urządzenie „${name}”.`,
    cardLooksGood: "Pasuje",
    cardEdit: "Edytuj",
    cardAriaLabel: "Potwierdź nazwę urządzenia",
    renameTitle: "Zmień nazwę tego urządzenia",
    renameLabel: "Nazwa urządzenia",
    renameSave: "Zapisz",
    renameCancel: "Anuluj",
    renameFailed: "Nie udało się zmienić nazwy. Spróbuj ponownie.",
  },

  collision: {
    title: (name: string): string => `Masz już „${name}”`,
    subtitle: "Jak nazwiemy to urządzenie?",
    chipWork: (name: string): string => `Służbowy ${name}`,
    chipHome: (name: string): string => `Domowy ${name}`,
    chipPersonal: (name: string): string => `Osobisty ${name}`,
    customLabel: "Własna nazwa",
    save: "Zapisz",
    useAnyway: "Użyj oryginalnej nazwy mimo to",
    saveFailed: "Nie udało się zapisać nowej nazwy. Spróbuj ponownie.",
    ariaLabel: "Rozwiąż konflikt nazw urządzeń",
  },

  clipboard: {
    title: "Schowek",
    empty: "Klipy z innych twoich urządzeń pojawiają się tutaj. Naciśnij, aby skopiować.",
    from: (label: string): string => `od ${label}`,
    copy: "Kopiuj do schowka",
    copied: "Skopiowano",
    reveal: "Pokaż",
    hide: "Ukryj",
    maskedHint: "Treść ukryta — naciśnij „Pokaż”, aby wyświetlić",
    sensitiveChip: "Poufne",
    cloudChip: "przez twój token API",
    sendPlaceholder: "Wklej tekst do wysłania…",
    sendCta: "Wyślij",
    sendSensitiveToggle: "Oznacz jako poufne",
    sendSuccess: (n: number): string =>
      `Wysłano na ${n} ${selectPlural("pl", n, {
        one: "urządzenie",
        few: "urządzenia",
        many: "urządzeń",
        other: "urządzenia",
      })}.`,
    sendPartial: (n: number, total: number): string =>
      `Dotarło do ${n}/${total} ${selectPlural("pl", total, {
        one: "urządzenia",
        few: "urządzeń",
        many: "urządzeń",
        other: "urządzenia",
      })}.`,
    sendNoDevices: "Brak sparowanych urządzeń do wysłania.",
    sendFailed: "Nie udało się wysłać. Spróbuj ponownie.",
    sendEmpty: "Nie ma nic do wysłania.",
    dockExpand: "Pokaż historię schowka",
    dockCollapse: "Zwiń",
    dockSendTitle: "Wyślij na swoje urządzenia",
    dockFromClipboard: (chars: number): string =>
      `Ze schowka · ${chars} ${selectPlural("pl", chars, {
        one: "znak",
        few: "znaki",
        many: "znaków",
        other: "znaku",
      })}`,
    dockSend: (n: number): string => `Wyślij na ${n}`,
    dockCancel: "Anuluj",
    dockRecentHeading: "Ostatnie",
    dockEmptyClipboard: "Twój schowek jest pusty.",
    shareTitle: "Wyślij na swoje urządzenia",
    shareReview:
      "Sprawdź, co wysyłasz. Trafi to na twoje sparowane urządzenia z szyfrowaniem end-to-end.",
    shareSend: "Wyślij",
    shareCancel: "Anuluj",
    shareNotPaired: "Aby wysłać udostępniony tekst, najpierw sparuj urządzenie.",
    shareOpenApp: "Otwórz Reflecto",
  },
  install: {
    title: "Zainstaluj Reflecto",
    body: "Dodaj do ekranu głównego, aby uzyskać szybszy dostęp.",
    cta: "Zainstaluj",
    dismissAria: "Zamknij monit o instalacji",
  },
  common: {
    loading: "Ładowanie",
  },
  errors: {
    notPaired: "Nie sparowano",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${browser} na ${os}`,
    webFallback: "Przeglądarka WWW",
  },

  palette: {
    placeholder: "Wpisz polecenie…",
    noResults: "Brak pasujących poleceń",
    ariaLabel: "Paleta poleceń",
    clearAll: "Usuń wszystkie powiadomienia",
    glance: "Podsumowanie Glance",
    cancelSnooze: "Anuluj wstrzymanie",
    copyClip: "Kopiuj ostatni odebrany klip",
  },
} satisfies MessagesShape;
